import { useEffect, useRef, useState } from "react";

// Leaflet via CDN sob demanda (sem dependência no bundle / sem API key).
const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";

declare global {
  interface Window {
    L?: any;
  }
}

type LatLng = [number, number];
type Point = { text: string; coords?: LatLng };

function loadLeaflet(): Promise<any> {
  return new Promise((resolve, reject) => {
    if (window.L) {
      resolve(window.L);
      return;
    }
    if (!document.querySelector("link[data-leaflet]")) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = LEAFLET_CSS;
      link.setAttribute("data-leaflet", "1");
      document.head.appendChild(link);
    }
    const existing = document.querySelector<HTMLScriptElement>("script[data-leaflet]");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.L));
      existing.addEventListener("error", () => reject(new Error("leaflet")));
      return;
    }
    const script = document.createElement("script");
    script.src = LEAFLET_JS;
    script.async = true;
    script.setAttribute("data-leaflet", "1");
    script.onload = () => resolve(window.L);
    script.onerror = () => reject(new Error("leaflet"));
    document.body.appendChild(script);
  });
}

async function nominatim(query: string): Promise<LatLng | null> {
  const url =
    "https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=br&q=" +
    encodeURIComponent(query);
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  const data = (await res.json()) as Array<{ lat: string; lon: string }>;
  if (!data || data.length === 0) return null;
  return [parseFloat(data[0].lat), parseFloat(data[0].lon)];
}

async function suggest(query: string): Promise<{ label: string; coords: LatLng }[]> {
  try {
    const url =
      "https://nominatim.openstreetmap.org/search?format=json&limit=5&countrycodes=br&q=" +
      encodeURIComponent(query);
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    const data = (await res.json()) as Array<{ lat: string; lon: string; display_name: string }>;
    return data.map((d) => ({ label: d.display_name, coords: [parseFloat(d.lat), parseFloat(d.lon)] as LatLng }));
  } catch {
    return [];
  }
}

type BrasilCep = {
  street?: string; neighborhood?: string; city?: string; state?: string;
  location?: { coordinates?: { latitude?: string | number; longitude?: string | number } };
};

async function fromCep(cep: string): Promise<LatLng | null> {
  const digits = cep.replace(/\D/g, "");
  if (digits.length !== 8) return null;
  try {
    const res = await fetch(`https://brasilapi.com.br/api/cep/v2/${digits}`);
    if (!res.ok) return null;
    const d = (await res.json()) as BrasilCep;
    const lat = d.location?.coordinates?.latitude;
    const lon = d.location?.coordinates?.longitude;
    if (lat != null && lon != null) return [Number(lat), Number(lon)];
    if (d.street && d.city) {
      const hit = await nominatim([d.street, d.neighborhood, d.city, d.state, "Brasil"].filter(Boolean).join(", "));
      if (hit) return hit;
    }
    if (d.city) return await nominatim([d.city, d.state, "Brasil"].filter(Boolean).join(", "));
    return null;
  } catch {
    return null;
  }
}

async function geocodeText(text: string): Promise<LatLng | null> {
  const t = text.trim();
  if (!t) return null;
  const cep = t.match(/\d{5}-?\d{3}/);
  if (cep) {
    const byCep = await fromCep(cep[0]);
    if (byCep) return byCep;
  }
  return nominatim(t.toLowerCase().includes("brasil") ? t : t + ", Brasil");
}

async function resolvePoint(p: Point): Promise<LatLng | null> {
  return p.coords || (await geocodeText(p.text));
}

function getGps(): Promise<LatLng | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve([p.coords.latitude, p.coords.longitude]),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  });
}

type OsrmStep = { name?: string; distance: number; maneuver?: { type?: string; modifier?: string } };
type OsrmRoute = {
  distance: number; duration: number;
  geometry: { coordinates: number[][] };
  legs?: Array<{ steps?: OsrmStep[] }>;
};

async function osrmRoute(points: LatLng[]): Promise<OsrmRoute | null> {
  const coords = points.map((p) => `${p[1]},${p[0]}`).join(";");
  const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson&steps=true`;
  const res = await fetch(url);
  const data = (await res.json()) as { routes?: OsrmRoute[] };
  return (data.routes && data.routes[0]) || null;
}

// Otimiza a ordem (origem fixa no início, destino no fim). Retorna os índices
// de entrada na ordem ótima.
async function osrmTripOrder(points: LatLng[]): Promise<number[] | null> {
  const coords = points.map((p) => `${p[1]},${p[0]}`).join(";");
  const url = `https://router.project-osrm.org/trip/v1/driving/${coords}?source=first&destination=last&roundtrip=false`;
  const res = await fetch(url);
  const data = (await res.json()) as { code?: string; waypoints?: Array<{ waypoint_index: number }> };
  if (data.code !== "Ok" || !data.waypoints) return null;
  return data.waypoints
    .map((w, i) => ({ i, pos: w.waypoint_index }))
    .sort((a, b) => a.pos - b.pos)
    .map((o) => o.i);
}

const DIRS: Record<string, string> = {
  left: "à esquerda", right: "à direita",
  "slight left": "levemente à esquerda", "slight right": "levemente à direita",
  "sharp left": "acentuadamente à esquerda", "sharp right": "acentuadamente à direita",
  straight: "em frente", uturn: "faça o retorno",
};

function instruction(step: OsrmStep): string {
  const type = step.maneuver?.type || "";
  const mod = step.maneuver?.modifier || "";
  const road = step.name ? ` na ${step.name}` : "";
  if (type === "arrive") return "Chegada ao destino";
  if (type === "depart") return `Siga${road}`;
  if (type === "turn" || type === "end of road") return `Vire ${DIRS[mod] || ""}${road}`.replace(/\s+/g, " ").trim();
  if (type === "continue" || type === "new name") return `Continue${road}`;
  if (type === "merge") return `Entre${road}`;
  if (type === "on ramp") return `Pegue o acesso${road}`;
  if (type === "off ramp") return `Pegue a saída${road}`;
  if (type === "fork") return `Mantenha-se ${DIRS[mod] || "em frente"}${road}`;
  if (type === "roundabout" || type === "rotary") return `Entre na rotatória${road}`;
  return `Siga${road}`;
}

function fmtKm(m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
}

function AddressInput(
  { value, placeholder, onChange }: { value: Point; placeholder: string; onChange: (p: Point) => void },
) {
  const [sugs, setSugs] = useState<{ label: string; coords: LatLng }[]>([]);
  const [open, setOpen] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  function onType(text: string) {
    onChange({ text, coords: undefined });
    if (timer.current) window.clearTimeout(timer.current);
    if (text.trim().length < 4) {
      setSugs([]);
      return;
    }
    timer.current = window.setTimeout(() => {
      void suggest(text).then((r) => {
        setSugs(r);
        setOpen(true);
      });
    }, 400);
  }

  return (
    <div className="ac-wrap">
      <input
        className="field"
        placeholder={placeholder}
        value={value.text}
        onChange={(e) => onType(e.target.value)}
        onFocus={() => sugs.length > 0 && setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
      />
      {open && sugs.length > 0 ? (
        <ul className="ac-list">
          {sugs.map((s, i) => (
            <li key={i} onMouseDown={() => { onChange({ text: s.label, coords: s.coords }); setSugs([]); setOpen(false); }}>
              {s.label}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function RouteMap(
  { leadId, defaultDestination, onClose }: { leadId: string; defaultDestination: string; onClose: () => void },
) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapObj = useRef<any>(null);
  const layer = useRef<any>(null);
  const SKEY = "wcrm-route-" + leadId;

  const [useGps, setUseGps] = useState(true);
  const [origin, setOrigin] = useState<Point>({ text: "" });
  const [stops, setStops] = useState<Point[]>([]);
  const [destination, setDestination] = useState<Point>({ text: defaultDestination });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [info, setInfo] = useState("");
  const [steps, setSteps] = useState<string[]>([]);
  const [links, setLinks] = useState<{ waze: string; gmaps: string } | null>(null);
  const [saved, setSaved] = useState(false);

  async function drawRoute(o: Point, s: Point[], d: Point, gps: boolean) {
    const L = window.L;
    const map = mapObj.current;
    if (!L || !map) return;
    setBusy(true);
    setStatus("Calculando a rota...");
    setSteps([]);
    setInfo("");
    setLinks(null);
    try {
      const originPt = gps ? await getGps() : await resolvePoint(o);
      if (!originPt) {
        setStatus(gps ? "Não consegui obter sua localização (GPS)." : "Não localizei o endereço de partida.");
        return;
      }
      const destPt = await resolvePoint(d);
      if (!destPt) {
        setStatus("Não localizei o endereço de destino.");
        return;
      }
      const stopPts: LatLng[] = [];
      for (const stop of s) {
        if (!stop.text.trim()) continue;
        const p = await resolvePoint(stop);
        if (p) stopPts.push(p);
      }
      const all: LatLng[] = [originPt, ...stopPts, destPt];

      if (layer.current) layer.current.clearLayers();
      else layer.current = L.layerGroup().addTo(map);
      const grp = layer.current;
      L.circleMarker(originPt, { radius: 8, color: "#2563eb", fillColor: "#2563eb", fillOpacity: 0.9 }).addTo(grp).bindPopup("Partida");
      stopPts.forEach((p, i) => {
        L.circleMarker(p, { radius: 7, color: "#d39312", fillColor: "#d39312", fillOpacity: 0.9 }).addTo(grp).bindPopup(`Parada ${i + 1}`);
      });
      L.circleMarker(destPt, { radius: 8, color: "#c2410c", fillColor: "#c2410c", fillOpacity: 0.9 }).addTo(grp).bindPopup("Destino");

      const route = await osrmRoute(all);
      if (route) {
        const line = L.polyline(route.geometry.coordinates.map((c) => [c[1], c[0]] as LatLng), { color: "#108b7c", weight: 5 }).addTo(grp);
        map.fitBounds(line.getBounds(), { padding: [30, 30] });
        const km = (route.distance / 1000).toFixed(1);
        const min = Math.round(route.duration / 60);
        setInfo(`${km} km · ~${min} min de carro`);
        const list: string[] = [];
        (route.legs || []).forEach((leg) => (leg.steps || []).forEach((st) => {
          const txt = instruction(st);
          list.push(st.distance > 0 ? `${txt} (${fmtKm(st.distance)})` : txt);
        }));
        setSteps(list);
      } else {
        map.fitBounds(L.latLngBounds(all), { padding: [30, 30] });
        setInfo("Não consegui traçar a rota; mostrando os pontos.");
      }

      const g =
        "https://www.google.com/maps/dir/?api=1&travelmode=driving" +
        `&origin=${originPt[0]},${originPt[1]}` +
        `&destination=${destPt[0]},${destPt[1]}` +
        (stopPts.length ? `&waypoints=${encodeURIComponent(stopPts.map((p) => `${p[0]},${p[1]}`).join("|"))}` : "");
      const w = `https://www.waze.com/ul?ll=${destPt[0]},${destPt[1]}&navigate=yes`;
      setLinks({ waze: w, gmaps: g });
      setStatus("");
    } catch {
      setStatus("Erro ao calcular a rota. Tente novamente.");
    } finally {
      setBusy(false);
    }
  }

  async function optimizeStops() {
    if (stops.filter((s) => s.text.trim()).length < 2) {
      setStatus("Adicione pelo menos 2 paradas para otimizar.");
      return;
    }
    setBusy(true);
    setStatus("Otimizando a ordem das paradas...");
    try {
      const o = useGps ? await getGps() : await resolvePoint(origin);
      const d = await resolvePoint(destination);
      if (!o || !d) {
        setStatus("Defina partida e destino válidos para otimizar.");
        return;
      }
      const valid: Point[] = [];
      const coords: LatLng[] = [];
      for (const s of stops) {
        if (!s.text.trim()) continue;
        const c = await resolvePoint(s);
        if (c) {
          valid.push({ text: s.text, coords: c });
          coords.push(c);
        }
      }
      if (coords.length < 2) {
        setStatus("Preciso de pelo menos 2 paradas válidas.");
        return;
      }
      const pts = [o, ...coords, d];
      const order = await osrmTripOrder(pts);
      if (!order) {
        setStatus("Não consegui otimizar agora.");
        return;
      }
      const last = pts.length - 1;
      const newStops = order.filter((idx) => idx !== 0 && idx !== last).map((idx) => valid[idx - 1]);
      setStops(newStops);
      await drawRoute(origin, newStops, destination, useGps);
    } finally {
      setBusy(false);
    }
  }

  function moveStop(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= stops.length) return;
    const next = stops.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setStops(next);
  }

  function saveRoute() {
    try {
      localStorage.setItem(SKEY, JSON.stringify({ useGps, origin, stops, destination }));
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } catch {
      /* ignora */
    }
  }

  useEffect(() => {
    let cancelled = false;
    let startUseGps = useGps;
    let startOrigin = origin;
    let startStops = stops;
    let startDest = destination;
    try {
      const raw = localStorage.getItem(SKEY);
      if (raw) {
        const s = JSON.parse(raw) as { useGps?: boolean; origin?: Point; stops?: Point[]; destination?: Point };
        if (typeof s.useGps === "boolean") { startUseGps = s.useGps; setUseGps(s.useGps); }
        if (s.origin) { startOrigin = s.origin; setOrigin(s.origin); }
        if (Array.isArray(s.stops)) { startStops = s.stops; setStops(s.stops); }
        if (s.destination) { startDest = s.destination; setDestination(s.destination); }
      }
    } catch {
      /* ignora */
    }
    (async () => {
      try {
        const L = await loadLeaflet();
        if (cancelled || !mapRef.current || mapObj.current) return;
        const map = L.map(mapRef.current).setView([-22.9, -43.2], 6);
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "© OpenStreetMap", maxZoom: 19,
        }).addTo(map);
        mapObj.current = map;
        setTimeout(() => map.invalidateSize(), 50);
        void drawRoute(startOrigin, startStops, startDest, startUseGps);
      } catch {
        setStatus("Erro ao carregar o mapa.");
      }
    })();
    return () => {
      cancelled = true;
      if (mapObj.current) {
        mapObj.current.remove();
        mapObj.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="route-overlay" onClick={(event) => { event.stopPropagation(); onClose(); }}>
      <div className="route-modal route-planner" onClick={(event) => event.stopPropagation()}>
        <header className="modal-head">
          <span className="modal-head-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 20l-5.5 2.5V6L9 3.5l6 2.5 5.5-2.5V18L15 20.5 9 18z" /><path d="M9 3.5V18M15 6v14.5" /></svg>
          </span>
          <div className="modal-head-text">
            <div className="modal-head-title">Planejar rota</div>
            <div className="modal-head-sub">Partida, paradas e destino</div>
          </div>
          <button className="modal-close" type="button" aria-label="Fechar" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </header>

        <div className="route-form">
          <div className="route-row">
            <span className="route-tag tag-origin">Partida</span>
            {useGps ? (
              <span className="route-gps">Minha localização (GPS)</span>
            ) : (
              <AddressInput value={origin} placeholder="Endereço ou CEP de partida" onChange={setOrigin} />
            )}
            <button className="btn btn-compact" type="button" onClick={() => setUseGps(!useGps)}>
              {useGps ? "Digitar" : "Usar GPS"}
            </button>
          </div>

          {stops.map((s, i) => (
            <div className="route-row" key={i}>
              <span className="route-tag tag-stop">Parada {i + 1}</span>
              <AddressInput value={s} placeholder="Endereço ou CEP da parada"
                onChange={(p) => setStops(stops.map((v, j) => (j === i ? p : v)))} />
              <button className="btn btn-compact" type="button" title="Subir" disabled={i === 0} onClick={() => moveStop(i, -1)}>↑</button>
              <button className="btn btn-compact" type="button" title="Descer" disabled={i === stops.length - 1} onClick={() => moveStop(i, 1)}>↓</button>
              <button className="btn btn-compact" type="button" title="Remover" onClick={() => setStops(stops.filter((_, j) => j !== i))}>×</button>
            </div>
          ))}

          <div className="route-row">
            <span className="route-tag tag-dest">Destino</span>
            <AddressInput value={destination} placeholder="Endereço ou CEP de destino" onChange={setDestination} />
          </div>

          <div className="route-form-actions">
            <button className="btn btn-secondary btn-compact" type="button" onClick={() => setStops([...stops, { text: "" }])}>+ Parada</button>
            <button className="btn btn-secondary btn-compact" type="button" onClick={() => void optimizeStops()} disabled={busy}>Otimizar paradas</button>
            <button className="btn btn-secondary btn-compact" type="button" onClick={saveRoute}>{saved ? "Salvo ✓" : "Salvar rota"}</button>
            <button className="btn btn-primary btn-compact" type="button" onClick={() => void drawRoute(origin, stops, destination, useGps)} disabled={busy}>
              {busy ? "Calculando..." : "Traçar rota"}
            </button>
          </div>
        </div>

        <div ref={mapRef} className="route-map" />

        {status ? <div className="route-status">{status}</div> : null}
        {info ? <div className="route-info">{info}</div> : null}

        {links ? (
          <div className="route-links">
            <a className="btn btn-secondary btn-compact" href={links.gmaps} target="_blank" rel="noopener">Abrir no Google Maps</a>
            <a className="btn btn-secondary btn-compact" href={links.waze} target="_blank" rel="noopener">Abrir no Waze</a>
          </div>
        ) : null}

        {steps.length ? (
          <ol className="route-steps">
            {steps.map((s, i) => (<li key={i}>{s}</li>))}
          </ol>
        ) : null}
      </div>
    </div>
  );
}
